<script setup lang="ts">
import { userModule } from '@/store/user/module'
import { computed, ref, onMounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { Vue3Lottie } from 'vue3-lottie'
import type { Study } from '@pocketprep/types'
import { screenModule } from '@/store/screen/module'
import IncompleteLevelBlob from '@/assets/levelup/IncompleteLevelBlob.vue'
import LMCompletedLevelBlob from '@/assets/levelup/light-mode/CompletedLevelBlob.vue'
import DMCompletedLevelBlob from '@/assets/levelup/dark-mode/CompletedLevelBlob.vue'
import MobileIncompleteLevelBlob from '@/assets/levelup/MobileIncompleteLevelBlob.vue'
import MobileLMCompletedLevelBlob from '@/assets/levelup/light-mode/MobileCompletedLevelBlob.vue'
import MobileDMCompletedLevelBlob from '@/assets/levelup/dark-mode/MobileCompletedLevelBlob.vue'
import MobileLMCompletedLevelUpBlob from '@/assets/levelup/light-mode/MobileCompletedLevelUpBlob.vue'
import MobileDMCompletedLevelUpBlob from '@/assets/levelup/dark-mode/MobileCompletedLevelUpBlob.vue'


const {
    Button: PocketButton,
} = UIKit

const { initialLevelProgress, scoreIncrease, levelsInSubjectCount, incompleteSubjectsCount } = defineProps<{
    initialLevelProgress: Study.Cloud.TLevelUpProgress[number] | null
    scoreIncrease: number
    levelsInSubjectCount: number
    incompleteSubjectsCount: number
}>()

const isLoading = ref(true)
const animation = ref()

const emit = defineEmits<{
    'continue': []
}>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const didLevelUp = computed(() => {
    if (!initialLevelProgress) {
        return false
    }
    return Boolean((
        initialLevelProgress.score + (scoreIncrease)
    ) >= initialLevelProgress.goal)
})

const startingLevel = computed(() => {
    return initialLevelProgress?.level || 1
})

const finalScore = computed(() => {
    return (
        ((initialLevelProgress?.score || 0) + (scoreIncrease))
        / (initialLevelProgress?.goal || Infinity)
    ) * 100
})

const isLastLevelInSubject = computed(() => {
    return initialLevelProgress?.level === levelsInSubjectCount
})

const isLastIncompleteSubject = computed(() => {
    return incompleteSubjectsCount === 1
})

const hasCompletedLevelUp = computed(() => {
    return isLastIncompleteSubject.value && didLevelUp.value
})

const progressMessage = computed(() => {
    let messages: { title: string; text: string }[] = []
    if (didLevelUp.value) {
        if (isLastLevelInSubject.value) {
            if (isLastIncompleteSubject.value) {
                messages = [{
                    title: 'All subjects leveled up!',
                    text: 'Brilliant work. You have unlocked your Recall Challenge.',
                }]
            } else {
                messages = [{
                    title: `${initialLevelProgress?.subjectName} leveled up!`,
                    text: `You completed all ${levelsInSubjectCount} difficulty levels. 
                    ${incompleteSubjectsCount - 1} more subject${
                        incompleteSubjectsCount - 1 > 1 ? 's' : ''
                    } before you unlock your Recall Challenge!`,
                }]
            }
        } else {
            messages = [{
                'title': 'Woohoo!',
                'text': `Level ${startingLevel.value} completed.`,
            },
            {
                'title': 'Shazam!',
                'text': `You completed Level ${startingLevel.value}.`,
            },
            {
                'title': 'Level cleared!',
                'text': 'Nicely done.',
            },
            {
                'title': 'Level tamed!',
                'text': '<img src="img/levelup/fire.png" alt=""> <img src="img/levelup/dragon.png" alt="">',
            },
            {
                'title': 'Way to go!',
                'text': `Level ${startingLevel.value} completed.`,
            },
            {
                'title': 'High-five!',
                'text': `Level ${startingLevel.value} complete!`,
            },
            {
                'title': 'Level completed.',
                'text': 'A+ work. <img src="img/levelup/smiley.png" alt="">',
            },
            {
                'title': 'Congrats!',
                'text': 'You\'re onto the next level.',
            },
            {
                'title': 'Level complete!',
                'text': 'Pass go & collect 200 metaphorical dollars.',
            },
            {
                'title': 'GGWP.',
                'text': 'You\'re onto the next level!',
            },
            {
                'title': 'Brilliant work.',
                'text': `Level ${startingLevel.value} cleared! Keep it up.`,
            },
            {
                'title': 'You did it!',
                'text': `Level ${startingLevel.value} complete. Well done.`,
            }]
        }
    } else if (finalScore.value < 25) {
        messages = [{
            title: 'Great start!',
            text: `You're on your way to completing Level ${startingLevel.value}.`,
        }, {
            title: 'It begins!',
            text: 'Progress started.',
        }, {
            title: 'Away we go.',
            text: `Great start on Level ${startingLevel.value}.`,
        }]
    } else if (finalScore.value < 50) {
        messages = [{
            title: 'Keep going.',
            text: 'You\'ve made a solid start. Keep it up.',
        }, {
            title: 'On your way.',
            text: 'Great progress! Keep going.',
        }, {
            title: 'Good work.',
            text: 'You\'re on the right track.',
        }]
    } else if (finalScore.value < 75) {
        messages = [{
            title: 'So close!',
            text: 'You\'re more than halfway there.',
        }, {
            title: 'Just about there.',
            text: 'Keep going - you\'re making great progress.',
        }, {
            title: 'Nicely done.',
            text: `Level ${startingLevel.value} is coming along nicely.`,
        }]
    } else {
        messages = [{
            title: 'Almost there!',
            text: `Just a few more correct questions 'til you beat Level ${startingLevel.value}.`,
        }, {
            title: 'Success is around the corner.',
            text: `You're super close to completing Level ${startingLevel.value}.`,
        }, {
            title: 'You\'re learning fast!',
            text: `Level ${startingLevel.value} is almost complete.`,
        }]
    }
    return messages[Math.floor(Math.random() * messages.length)]
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
    ])

    animation.value = await importAnimationJSONs()
    isLoading.value = false
})

const importAnimationJSONs = async () => {
    // completed all of level up
    if (hasCompletedLevelUp.value) {
        const completedLevelUpModule =
            await import(`@/assets/levelup/animation-json/CompletedLevelUp${isDarkMode.value ? 'DM' : 'LM'}.json`)
        return completedLevelUpModule.default
    }

    // completed subject
    if (isLastLevelInSubject.value && didLevelUp.value) {
        const completedSubjectModule =
            await import(`@/assets/levelup/animation-json/CompletedSubject${isDarkMode.value ? 'DM' : 'LM'}.json`)
        return completedSubjectModule.default
    }

    // completed level
    if (didLevelUp.value) {
        const completedLevelModule =
            await import(`@/assets/levelup/animation-json/CompletedLevel${isDarkMode.value ? 'DM' : 'LM'}.json`)
        return completedLevelModule.default
    }

    const incompleteLevelModule =
        await import(`@/assets/levelup/animation-json/IncompleteLevel${isDarkMode.value ? 'DM' : 'LM'}.json`)
    return incompleteLevelModule.default

}
</script>

<template>
    <div
        v-dark
        class="level-up-result"
        :class="{
            'level-up-result--incomplete-level': !didLevelUp,
        }"
        :tabindex="0"
        @mousedown.prevent
    >
        <div v-if="!isLoading" class="level-up-result__animation">
            <div v-if="breakpoint !== 'black-bear'">
                <DMCompletedLevelBlob
                    v-if="didLevelUp && isDarkMode"
                    class="level-up-result__animation-blob"
                />
                <LMCompletedLevelBlob
                    v-else-if="didLevelUp"
                    class="level-up-result__animation-blob"
                />
                <IncompleteLevelBlob
                    v-else
                    class="level-up-result__animation-blob"
                />
            </div>
            <div v-else>
                <MobileDMCompletedLevelUpBlob
                    v-if="hasCompletedLevelUp && isDarkMode"
                    class="level-up-result__mobile-animation-blob"
                    :class="{
                        'level-up-result__mobile-animation-blob--completed-level-up': hasCompletedLevelUp,
                    }"
                />
                <MobileLMCompletedLevelUpBlob
                    v-else-if="hasCompletedLevelUp"
                    class="level-up-result__mobile-animation-blob"
                    :class="{
                        'level-up-result__mobile-animation-blob--completed-level-up': hasCompletedLevelUp,
                    }"
                />
                <MobileDMCompletedLevelBlob
                    v-else-if="didLevelUp && isDarkMode"
                    class="level-up-result__mobile-animation-blob"
                />
                <MobileLMCompletedLevelBlob
                    v-else-if="didLevelUp"
                    class="level-up-result__mobile-animation-blob"
                />
                <MobileIncompleteLevelBlob
                    v-else
                    class="level-up-result__mobile-animation-blob"
                />
            </div>

            <div class="level-up-result__lottie-animation">
                <Vue3Lottie
                    :animationData="animation"
                    :height="280"
                    :width="280"
                    :loop="false"
                    :delay="1000"
                />
            </div>
        </div>
        <div v-if="!isLoading && progressMessage" class="level-up-result__progress-message">
            <div
                v-dark
                class="level-up-result__title"
                v-html="progressMessage.title"
            />
            <div
                v-dark
                class="level-up-result__message"
                v-html="progressMessage.text"
            />
        </div>
        <PocketButton
            v-if="!isLoading"
            :is-dark-mode="isDarkMode"
            @click="emit('continue')"
        >
            Continue
        </PocketButton>
    </div>
</template>

<style lang="scss" scoped>
.level-up-result {
    position: relative;
    overflow: hidden;
    outline: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 60px;
    background-color: $barely-green;
    height: 100%;
    box-sizing: border-box;
    border-radius: 6px;

    @include breakpoint(black-bear) {
        padding-top: 165px;
    }


    &--incomplete-level {
        background-color: $barely-purple;
    }

    &--dark {
        background-color: $charcoal;
    }

    &__lottie-animation {
        position: relative;
    }

    &__animation-blob,
    &__mobile-animation-blob {
        position: absolute;
        left: 0;
        right: 0;
        margin: auto;
    }

    &__mobile-animation-blob {
        position: absolute;
        top: 48px;
        left: -145px;
        width: 655px;
        height: 405px;

        &--completed-level-up {
            width: 100%;
            top: -40px;
            left: 0;
        }
    }

    &__progress-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 48px;
        text-align: center;
    }

    &__title {
        color: $brand-black;
        font-size: 18px;
        font-weight: 600;
        line-height: 25px;
        margin-bottom: 8px;

        &--dark {
            color: $white;
        }
    }

    &__message {
        color: $brand-black;
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
        margin-bottom: 24px;
        width: 280px;

        &--dark {
            color: $white;
        }

        :deep(img) {
            height: 20px;
            margin-left: 3px;
            display: inline;
            margin-bottom: -4px;
        }
    }
}
</style>
